import React, { useState, useMemo, useRef, useEffect } from 'react';
import Stepper from '@/v2/components/Stepper';
import {
  useCartState,
  useCartDispatch,
} from '../../../components/context/CartContext/CartContext';
import useFetch from 'use-http';
import { exponeaCapture } from '../../../components/hooks/exponeaCapture';
import { useCartData } from '../CartPage/context/CartDataContext';
import { useParams } from 'react-router-dom';
import ComplexBuyerInfo from './Components/ComplexBuyerInfo';
import { ComplexPriceStep } from './Components/ComplexPriceStep';
import ComplexPatientInfoStep from './Steps/ComplexPatientInfoStep';
import { useRegistration } from '../CartPage/context/RegistrationContext';
import { useUserState } from '@/components/context/UserContext';
import personalCodeValidation from '@/components/hooks/personalCodeValidation';
import SuccessStep from '../CartPage/Steps/SuccessStep';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import SuccessStepComplex from './Steps/SuccessStepComplex';
import RegistrationErrorDialog from '../CartPage/Components/RegistrationErrorDialog';

const ComplexCartPage = () => {
  const { complex, loading, setLoading, complexPrice } = useCartData();
  const { userInfo } = useRegistration();
  const dispatch = useCartDispatch();
  const buyerFormRef = useRef();
  const { request: registrationRequest, response: registrationResponse } =
    useFetch('/Registrations/ComplexServiceRegistration');
  const GENERIC_ERROR_MSG =
    'Atsiprašome, tačiau jūsų registracija nepavyko dėl nenumatytos klaidos. Bandykite dar kartą.';

  // Error dialog state (for POST errors in natural flow)
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] =
    useState(GENERIC_ERROR_MSG);
  const {
    request: simpleRegistrationRequest,
    response: simpleRegistrationResponse,
  } = useFetch('/Registrations/SimpleComplexRegistration');
  const [stepIndex, setStepIndex] = useState(0);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const { step } = useParams();
  const cartState = useCartState();
  const stepTitles = useMemo(
    () => ['Padalinys', 'Mokėtojo duomenys', 'Apmokėjimas'],
    [],
  );
  const [, , , birthDateFromPersonalCode] = personalCodeValidation();

  const userState = useUserState();
  const history = useHistory();

  useEffect(() => {
    if (complex == null && step !== 'success') {
      if (cartState.registrationId) {
        history.push('/payment/status/cancel');
      } else {
        history.push('/kompleksines-paslaugos');
      }
    }
  }, [complex, history, step]);

  const nextStep = async () => {
    setStepIndex((prev) => {
      const next = Math.min(prev + 1, steps.length - 1);

      if (prev === 0) {
        // Step 0 → just move forward
        return next;
      }

      if (prev === 1) {
        setLoading(true);
        showPaymentOptions(userInfo);
      }

      return next;
    });
  };

  const steps = useMemo(
    () => [
      {
        title: stepTitles[0],
        content: (
          <ComplexPriceStep prices={complex?.prices} goNext={nextStep} />
        ),
      },
      {
        title: stepTitles[1],
        content: (
          <ComplexPatientInfoStep
            ref={buyerFormRef}
            goNext={(formData) => {
              showPaymentOptions(formData);
            }}
          />
        ),
      },
      {
        title: stepTitles[2],
        content: <SuccessStep />,
      },
    ],
    [stepTitles, complex, complexPrice, userInfo],
  );

  useEffect(() => {
    if (step === 'success') {
      dispatch({ type: 'RESET_REGISTRATION_ID' });
      setStepIndex(stepTitles.length);
    }
  }, [step, stepTitles.length]);

  const isCompleted = stepIndex === steps.length;
  useEffect(() => {
    const unblock = history.block(() => {
      const nextPath = window.location.pathname;
      if (
        nextPath === '/kompleksines-paslaugos' &&
        stepIndex > 0 &&
        step !== 'success'
      ) {
        setStepIndex((prev) => prev - 1);
        return false;
      }

      return true;
    });

    return () => unblock();
  }, [history, stepIndex]);
  const prevStep = () => setStepIndex((i) => Math.max(i - 1, 0));

  async function showPaymentOptions(formData) {
    setFormSubmitting(true);
    setLoading(true);
    const payload = {
      patientEmail: formData.payerEmail,
      patientName: formData.payerFirstName,
      patientPhoneNumber: formData.payerPhone,
      patientSurname: formData.payerLastName,
      patientIdNumber: userState.isLoggedIn
        ? userState.personalCode
        : formData.payerPersonalCode,
      amount: complexPrice.price,
      complexServiceId: complex.service.serviceId,
      departmentId: complexPrice.departmentId,
      //   price: !!discountCodePrice
      //     ? complexPrice.discountCodePrice
      //     : complexPrice.price,
      price: complexPrice.price,
      giftCouponNumbers: complexPrice.appliedCoupons ?? [],
      discountCode: complexPrice.discountCode,
      priceId: complexPrice.priceId,
      childIdNumber: formData.patientPersonalCode,
      childName: formData.patientFirstName,
      childSurname: formData.patientLastName,
      childEspbiEsi: formData.selectedChildEspbiEsi,
      childBirthDate: formData.patientPersonalCode
        ? birthDateFromPersonalCode(formData.patientPersonalCode)
        : '',
      frontUrls: {
        cancelFrontURL: '/payment/status/cancel',
        acceptFrontUrl: '/kompleksines-paslaugos/cart/success',
        errorFrontUrl: '/complex-payment/error',
      },
    };

    // Optional: remove extra fields if needed
    delete payload.agreement2;
    delete payload.agreement3;

    const baseObj = [
      { name: 'category_1', value: 'Paslaugų paketai' },
      { name: 'category_2', value: complex.service.title },
      { name: 'Firstname', value: payload.patientName },
      { name: 'Lastname', value: payload.patientSurname },
      { name: 'email', value: payload.patientEmail },
      { name: 'Count', value: payload.count },
      { name: 'Price', value: payload.amount * payload.count },
      { name: 'Payment', value: 'Paysera' },
      { name: 'product_id', value: complex.service.serviceId },
      { name: 'category_id', value: 3 },
      { name: 'mobile_phone', value: payload.patientPhoneNumber },
    ];

    // SIMPLE WORK FLOW
    if (complex.simpleWorkflow) {
      await simpleRegistrationRequest.post('', payload);
      setLoading(false);

      if (simpleRegistrationResponse.status > 299) {
        setErrorDialogMessage(GENERIC_ERROR_MSG);
        setErrorDialogOpen(true);
      } else history.push('/kompleksines-paslaugos/cart/success');
    } else {
      try {
        const responseData = await registrationRequest.post('', payload);

        baseObj.push({
          name: 'booking_booking_id',
          value: responseData.couponOrderId,
        });
        baseObj.push({
          name: 'booking_payment_gateway_id',
          value: responseData.couponOrderId,
        });
        baseObj.push({
          name: 'purchase_total_cost',
          value: payload.amount * payload.count,
        });

        if (registrationResponse.status >= 500) {
          baseObj.push({ name: 'Status', value: 'Failed' });
          baseObj.push({ name: 'booking_stage', value: 'Cancelled' });
          exponeaCapture(baseObj, 'booking');
          // setErrorModalVisible(true);
          setErrorDialogMessage(GENERIC_ERROR_MSG);
          setErrorDialogOpen(true);
        } else if (registrationResponse.status >= 400) {
          const { errors } = responseData;
          baseObj.push({ name: 'Status', value: 'Failed' });
          baseObj.push({ name: 'booking_stage', value: 'Cancelled' });
          exponeaCapture(baseObj, 'booking');
          setErrorDialogMessage(GENERIC_ERROR_MSG);
          setErrorDialogOpen(true);
          // handle form errors here if needed
        } else {
          sessionStorage.setItem('exponeaTrack', JSON.stringify(baseObj));
          exponeaCapture(baseObj, 'booking_item');

          const payseraUrl = responseData.paymentUrl;
          setLoading(false);

          if (!payseraUrl && responseData.couponOrderId) {
            history.push('/kompleksines-paslaugos/cart/success');
          } else {
            dispatch({
              type: 'SET_REGISTRATION_ID',
              registrationId: responseData.couponOrderId,
              orderType: 'complex',
            });
            window.location.href = payseraUrl;
          }
        }
      } finally {
        setFormSubmitting(false);
      }
    }
  }

  return (
    <div className="w-full max-w-[1440px] mx-auto h-full p-4 font-primary">
      <div className="mb-4">
        <Stepper
          steps={stepTitles}
          currentStep={isCompleted ? steps.length + 1 : stepIndex + 1}
        />
      </div>

      <div className="pb-4 min-h-40">
        {isCompleted ? <SuccessStepComplex /> : steps[stepIndex]?.content}
      </div>

      <RegistrationErrorDialog
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        message={errorDialogMessage}
      />
    </div>
  );
};

export default ComplexCartPage;
